import PerfectScrollbar from "perfect-scrollbar";
import HcsToolbar from "./toolbar";
import Bowser from "bowser";

/**
 * @class
 * @classdesc Handle all UI dependent stuff.
 */
export default class HcsUiManager
{
    constructor(paramObj) {
        this.container = paramObj.container;
        this.hcs = paramObj.hcs;
        this.outsideScrollLock = paramObj.outsideScrollLock;

        this.resizeTirggerEvents = [];

        this.browser = Bowser.getParser(window.navigator.userAgent).parsedResult;

        this.navScrollbar = null;
        this.configScrollbar = null;

        this.container.classList.add("hcs-browser-" + this.browser.browser.name.replace(/ /g, "_").toLowerCase());
        this.container.classList.add("hcs-browser-" + this.browser.platform.type.toLowerCase());

        this.resizeTimeout = null;
    }

    init() {
        let self = this;

        this.toolbar = new HcsToolbar({
            hcs: this.hcs
        });

        // Disable scrolling of entire website so that scrolling inside HCS application
        // keep configurator in place.
        if(this.outsideScrollLock) {
            this.container.addEventListener("mouseenter", () => {
                let marginRightPx = 0;
                let scrollbarWidthPx = window.innerWidth - document.body.clientWidth;

                if(window.getComputedStyle) {
                    let bodyStyle = window.getComputedStyle(document.body);

                    if(bodyStyle) {
                        marginRightPx = parseInt(bodyStyle.marginRight, 10);
                    }
                }

                document.body.classList.add("hcs-scroll-lock");

                Object.assign(document.body.style, {
                    marginRight: `${marginRightPx + scrollbarWidthPx}px`
                });
            });

            this.container.addEventListener("mouseleave", () => {
                document.body.classList.remove("hcs-scroll-lock");

                Object.assign(document.body.style, {
                    marginRight: "auto"
                });
            });
        }
        
        document.addEventListener("fullscreenchange", () => this.fullScreenChanged.call(this), false);
        document.addEventListener("mozfullscreenchange", () => this.fullScreenChanged.call(this), false);
        document.addEventListener("webkitfullscreenchange", () => this.fullScreenChanged.call(this), false);
        document.addEventListener("MSFullscreenChange", () => this.fullScreenChanged.call(this), false);

        // Build container observer to detect resize event
        new MutationObserver(function(mutations) {
            let el = mutations[0].target;
            let w = el.clientWidth;
            let h = el.clientHeight;

            let isChange = mutations
                .map((m) => m.oldValue + '')
                .some((prev) => prev.indexOf("width: " + w + "px") === -1 || prev.indexOf("height: " + h + "px") === -1);

            if(!isChange)
                return;

            let event = new CustomEvent("resize");
            el.dispatchEvent(event);
        }).observe(this.container, { attributes: true, attributeOldValue: true, attributeFilter: ["style"] });

        // When the document body is our container we must catch the window resize event
        (document.body === this.container ? window : this.container).addEventListener("resize", () => {
            self.containerResized.call(self);
        });

        window.addEventListener("orientationchange", () => {
            self.containerResized.call(self, { delay: true });
        });

        this.containerResized.call(this);
    }

    /**
     * Returns browser-dependent mouse wheel speed for custom scrollbars.
     * @returns {number} Scroll speed for mouse wheel event.
     */
    getMouseWheelSpeed() {
        let mwSettings = this.hcs.settings.get("SETTINGS.SCROLLBAR_WHEEL_SPEED", "json");

        if(mwSettings) {
            try {
                let mouseWheelSpeedBrowser = mwSettings[this.browser && this.browser.browser.name.toLowerCase()];
                let mouseWheelSpeedGlobal = mwSettings["*"];

                let mouseWheelSpeed = mouseWheelSpeedBrowser || mouseWheelSpeedGlobal;

                if(mouseWheelSpeed) {
                    if(isNaN(parseFloat(mouseWheelSpeed))) {
                        throw "Mouse wheel speed is not a number: " + mouseWheelSpeed;
                    }

                    return parseFloat(mouseWheelSpeed);
                }
            } catch(e) {
                console.error("Error evaluating 'SETTINGS.SCROLLBAR_WHEEL_SPEED': " + e.message);
            }
        }

        return 1;
    }

    /**
     * Initialize scrollbar for main configuration area.
     * Replaces the browser scrollbar with a UI styled one.
     * @param {bool} [disableMouseWheel=false] - Enable/disable scrolling via mouse wheel.
     */
    configScrollbarInit(disableMouseWheel = false) {
        this.configScrollbarDisableMouseWheel = disableMouseWheel;

        let configContainer = this.container.querySelector(".hcs-wrapper");
        let scrollbarInitialized = configContainer !== null && configContainer.classList.contains("ps");

        if(configContainer !== null) {
            if(!scrollbarInitialized) {
                this.configScrollbar = new PerfectScrollbar(configContainer, {
                    suppressScrollX: true,
                    wheelSpeed: this.getMouseWheelSpeed(),
                    wheelPropagation: false,
                    handlers: disableMouseWheel ?
                        ["click-rail", "drag-thumb", "keyboard", "touch"] :
                        ["click-rail", "drag-thumb", "keyboard", "wheel", "touch"]
                        
                });
            } else {
                this.configScrollbar.update();
            }
        }
    }

    /**
     * Enable/disable scrolling via mouse wheel for config scrollbar.
     * @param {bool} disable - "true" to disable whell scrolling.
     */
    configScrollbarDisableWheelScroll(disable) {
        if(this.configScrollbar && this.configScrollbarUseMouseWheel !== disable) {
            this.configScrollbar.destroy();
            this.configScrollbarInit(disable);
        }
    }

    /**
     * Update scrollbar of configuration area.
     * This call is required when main container size changes.
     * Will be automatically called in {@link HcsUiManager#containerResized}
     * @param {object} [scrollToElement] - When DOM element is specified the config
     * area will scroll to this element.
     */
    configScrollbarUpdate(scrollToElement) {
        let configContainer = this.container.querySelector(".hcs-wrapper");
        let scrollbarInitialized = configContainer.classList.contains("ps");

        if(scrollbarInitialized) {
            if(scrollToElement) {
                this.scrollParentToChild(configContainer, scrollToElement);
            } else {
                configContainer.scrollTop = 0;
            }

            this.configScrollbar.update();
        }
    }

    /**
     * Scroll given element into view inside given container.
     * Instead of scrollIntoView() function which (may) use window/body
     * to scroll the given element into view area this function uses an outer
     * container being scrolled.
     * See also: https://app.asana.com/0/790817394557022/1177033383961035/f
     * @param {DOMElement} parent - Outer container which shall scroll the given element into view.
     * @param {DOMElement} child - Element to scroll to.
     */
    scrollParentToChild(parent, child) {
        // Where is the parent on page
        var parentRect = parent.getBoundingClientRect();
        // What can you see?
        var parentViewableArea = {
            height: parent.clientHeight,
            width: parent.clientWidth
        };

        // Where is the child
        var childRect = child.getBoundingClientRect();
        // Is the child viewable?
        var isViewable = (childRect.top >= parentRect.top) && (childRect.top <= parentRect.top + parentViewableArea.height);

        // if you can't see the child try to scroll parent
        if(!isViewable) {
            // scroll by offset relative to parent
            parent.scrollTop = (childRect.top + parent.scrollTop) - parentRect.top
        }
    }

    /**
     * Initialize scrollbar for configuration groups.
     * Hides all scrollbars and only shows an up/down indicator when content is scrolling.
     * Scrolling is only possible with mouse wheel on desktop PCs or via touch scroll on mobile devices.
     */
    navScrollbarInit() {
        let navContainer = this.container.querySelector(".hcs-nav");

        if(this.navScrollbar === null && navContainer !== null) {
            let scrollbarActive = navContainer.classList.contains("ps--active-y");

            let navScrollTop = this.container.querySelector(".hcs-nav .hcs-scroll-top");
            let navScrollBottom = this.container.querySelector(".hcs-nav .hcs-scroll-bottom");

            this.navScrollbar = new PerfectScrollbar(navContainer, {
                suppressScrollX: true,
                wheelSpeed: this.getMouseWheelSpeed(),
                wheelPropagation: false
            });

            navContainer.addEventListener("ps-y-reach-end", () => {
                navScrollBottom.classList.remove("hcs-show");
                navScrollTop.classList.add("hcs-show");
            });

            navContainer.addEventListener("ps-y-reach-start", () => {
                navScrollTop.classList.remove("hcs-show");
                navScrollBottom.classList.add("hcs-show");
            });

            navContainer.addEventListener("ps-scroll-down", () => {
                navScrollTop.classList.add("hcs-show");
            });

            navContainer.addEventListener("ps-scroll-up", () => {
                navScrollBottom.classList.add("hcs-show");
            });

            if(scrollbarActive) {
                navScrollBottom.classList.add("hcs-show");
            }
        }
    }

    /**
     * Update navigation scrollbar.
     * This call is required when main container size changes.
     * Will be automatically called in {@link HcsUiManager#containerResized}
     */
    navScrollbarUpdate() {
        let navContainer = this.container.querySelector(".hcs-nav");

        if(this.navScrollbar !== null) {
            this.navScrollbar.update();

            if(navContainer && navContainer.scrollTop === 0) {
                let navScrollTop = this.container.querySelector(".hcs-nav .hcs-scroll-top");
                navScrollTop.classList.remove("hcs-show");
            }
        }
    }

    /**
     * Toggle between normal and fullscreen mode.
     */
    toggleFullscreen() {
        if(!this.fullscreenActive) {
            this.hcs.api.logToolbarOption("Fullscreen");

            if(this.container.requestFullscreen) {
                this.container.requestFullscreen();
            } else if(this.container.mozRequestFullScreen) {
                this.container.mozRequestFullScreen();
            } else if(this.container.msRequestFullscreen) {
                this.container.msRequestFullscreen();
            } else if(this.container.webkitRequestFullscreen) {
                this.container.webkitRequestFullscreen();
            }
        } else {
            if(document.exitFullscreen) {
                document.exitFullscreen();
            } else if(document.msExitFullscreen) {
                document.msExitFullscreen();
            } else if(document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if(document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
        }
    }

    /**
     * Check current fullscreen state.
     * @returns {bool} Returns true when currently in fullscreen mode, otherwise false.
     */
    get fullscreenActive() {
        return (typeof (document.fullscreenElement) !== "undefined" && document.fullscreenElement !== null) ||
            (typeof (document.webkitFullscreenElement) !== "undefined" && document.webkitFullscreenElement !== null) ||
            (typeof (document.mozFullScreenElement) !== "undefined" && document.mozFullScreenElement !== null) ||
            (typeof (document.msFullscreenElement) !== "undefined" && document.msFullscreenElement !== null);
    }

    /**
     * Called when fullscreen mode has changed.
     */
    fullScreenChanged() {
        if(this.fullscreenActive) {
            this.container.classList.add("hcs-fullscreen");
            this.container.querySelector("hcs-toolbar-button.hcs-button-fullscreen").classList.add("hcs-active");
        } else {
            this.container.classList.remove("hcs-fullscreen");
            this.container.querySelector("hcs-toolbar-button.hcs-button-fullscreen").classList.remove("hcs-active");
        }

        this.containerResized({ delay: true });
    }

    /**
     * Returns current HCS container size.
     * @returns {object} - Container size object "width" and "height".
     */
    size() {
        return {
            width: this.container.offsetWidth,
            height: this.container.offsetHeight
        };
    }

    /**
     * Add UI size change trigger.
     * @param {function} eventFunction Function being called when UI size has changed.
     */
    onUiResized(eventFunction) {
        this.resizeTirggerEvents.push(eventFunction);
    }

    /**
     * Simple bootstrap-like dynamic UI size handling.
     * Depending and the container size we'll add a special class which will change the UI layout.
     */
    bootstrap() {
        if(this.fullscreenActive) {
            this.container.classList.add("hcs-fullscreen");
        } else {
            this.container.classList.remove("hcs-fullscreen");
        }

        switch(true) {
            case this.size().width < 1030:
                this.container.classList.remove("hcs-bts-1200");
                this.container.classList.add("hcs-bts-960");
                break;

            default:
                this.container.classList.remove("hcs-bts-960");
                this.container.classList.add("hcs-bts-1200");
                break;
        }

        // ASANA https://app.asana.com/0/1200195525726843/1200345706349536
        // Show parts list as centered popup when configurator size is below this value.
        // Othwise show parts list as right sidebar.
        if(this.size().width < 1470) {
            this.container.classList.add("hcs-parts-list-compact");
        } else {
            this.container.classList.remove("hcs-parts-list-compact");
        }
    }

    /**
     * Must be called everytime the main HCS container is being resized
     * to maintain inner controls like scrollbars and ccAO canvas.
     * @param {Object} [options] - Options
     * @param {bool} [options.delay=true] - Some resize operations require some delay until container size could
     * be successfully determined. Especially on tablets when changing to fullscreen and back.
     */
    containerResized(options) {
        let opts = Object.assign({}, {
            delay: false
        }, options);

        let self = this;

        var resize = function() {
            self.bootstrap();

            if(self.navScrollbar !== null) {
                self.navScrollbarUpdate();
            }

            if(self.configScrollbar !== null) {
                self.configScrollbarUpdate();
            }

            if(typeof self.hcs.cad !== "undefined") {
                self.hcs.cad.autoResize();
            }

            if(self.hcs.cad.ccAO.dimensions) {
                self.hcs.cad.ccAO.dimensions.updateDimensions2D();
            }

            self.resizeTirggerEvents.forEach(eventFunction => {
                eventFunction.call(this, {
                    size: self.size()
                });
            }, this);
        };

        if(opts.delay) {
            if(this.resizeTimeout !== null) {
                clearTimeout(this.resizeTimeout);
            }

            this.resizeTimeout = setTimeout(function() {
                resize();
            }, 200);
        } else {
            resize();
        }
    }
}